/**
 * ArticlesLaunch — public 1-pager at /whats-new/articles-pages.
 * Doctor-friendly copy. Zero technical jargon.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Newspaper, Search, FileText, Printer, ArrowRight, Stethoscope, BookOpen, MapPin, Eye } from 'lucide-react';

const LAUNCH = {
  slug: 'articles-pages',
  title: 'Your health articles can now show up on Google search',
  published_at: '2026-02-10',
  summary: 'Every article you publish now lives on its own dedicated page on your DocSite — written under your name, ranked under your name. When a patient searches "chest pain symptoms in Mumbai", your article can be the first thing they read.',
  keywords: [
    'doctor articles patients find me',
    'health articles for patients India',
    'doctor SEO articles',
    'medical insights doctor website',
    'DocSite article pages',
  ],
};

export default function ArticlesLaunch() {
  const url = `${window.location.origin}/whats-new/${LAUNCH.slug}`;
  const articleJsonLd = {
    '@context': 'https://schema.org', '@type': 'NewsArticle',
    headline: LAUNCH.title, description: LAUNCH.summary, datePublished: LAUNCH.published_at,
    keywords: LAUNCH.keywords.join(', '), mainEntityOfPage: url,
    publisher: { '@type': 'Organization', name: 'DocSite', logo: { '@type': 'ImageObject', url: `${window.location.origin}/logo512.png` } },
  };
  const breadcrumbJsonLd = {
    '@context': 'https://schema.org', '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: "What's New", item: `${window.location.origin}/whats-new` },
      { '@type': 'ListItem', position: 2, name: LAUNCH.title, item: url },
    ],
  };

  return (
    <div className="min-h-screen bg-white text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
      <Helmet>
        <title>{`${LAUNCH.title} · DocSite`}</title>
        <meta name="description" content={LAUNCH.summary} />
        <meta name="keywords" content={LAUNCH.keywords.join(', ')} />
        <link rel="canonical" href={url} />
        <meta property="og:title" content={LAUNCH.title} />
        <meta property="og:description" content={LAUNCH.summary} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={url} />
        <meta property="article:published_time" content={LAUNCH.published_at} />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
      </Helmet>

      <style>{`@media print { .no-print { display: none !important; } body { background: white; } }`}</style>

      <div className="max-w-4xl mx-auto px-6 py-10">
        <div className="flex items-center justify-between mb-8 no-print">
          <Link to="/whats-new" className="text-sm text-sky-600 hover:underline">← All updates</Link>
          <button onClick={() => window.print()} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700">
            <Printer size={12} />Print / Save as PDF
          </button>
        </div>

        <div className="mb-10">
          <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-sky-600 mb-3">Just Launched · 10 Feb 2026</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-medium leading-[1.1] tracking-tight mb-5" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Patients searching their symptoms <span className="text-sky-600">can now land on your article first.</span>
          </h1>
          <p className="text-lg text-slate-600 max-w-3xl leading-relaxed">{LAUNCH.summary}</p>
        </div>

        <div className="grid sm:grid-cols-3 gap-4 mb-12">
          <Card icon={<Newspaper size={18} className="text-sky-600" />} title="Your own article library">
            Patients can now visit a single page that lists every article you've written — like a personal magazine of your medical voice. Easy to share, easy to bookmark.
          </Card>
          <Card icon={<FileText size={18} className="text-sky-600" />} title="Each article has its own page">
            Every article gets its own clean web address. Patients can share the link on WhatsApp, family groups, or paste it on Google — and it always opens cleanly.
          </Card>
          <Card icon={<Search size={18} className="text-sky-600" />} title="Found on Google">
            When someone in your city searches a related symptom or condition, Google can now point them to your article — you become the trusted voice they read first.
          </Card>
        </div>

        <Section title="What changed today" icon={<BookOpen size={18} />}>
          <ul className="space-y-3 text-slate-700 text-sm">
            <Item>Article cards on your profile are now <b>clickable and actually open</b>. Earlier they were going to a "Page Not Found" — fixed.</Item>
            <Item>Above each article, you now see a <b>simple breadcrumb trail</b> ("Dr. Name → Articles → This article") so the patient never feels lost and can come back to your profile in one click.</Item>
            <Item>The <b>"See all my articles"</b> link from your profile now opens a proper page listing every article you've written.</Item>
            <Item>Google now sees your articles as <b>full medical content from a real doctor</b> — meaning your search rank goes up over time.</Item>
          </ul>
        </Section>

        <Section title="Why this matters for your practice" icon={<Eye size={18} />}>
          <div className="grid sm:grid-cols-2 gap-4">
            <FactRow icon={<Search size={14} className="text-sky-600" />} title="Patients find you before competitors">
              They Google "knee pain treatment Pune" — they find your article — they realise you're the local expert — they book.
            </FactRow>
            <FactRow icon={<MapPin size={14} className="text-sky-600" />} title="Local + niche = fewer rivals">
              "Hair fall solution Bandra" or "diabetes diet plan Lucknow" — these searches have far less competition than generic terms. Your articles target exactly these.
            </FactRow>
            <FactRow icon={<Stethoscope size={14} className="text-sky-600" />} title="Builds your authority">
              A patient who reads your article before walking in already trusts your judgment. Consultations get smoother, follow-ups get easier.
            </FactRow>
            <FactRow icon={<Newspaper size={14} className="text-sky-600" />} title="Compounds month after month">
              The more articles you publish, the more search terms you cover. Six months in, your DocSite shows up on dozens of patient searches every day.
            </FactRow>
          </div>
        </Section>

        <div className="mt-10 bg-gradient-to-br from-sky-50 to-emerald-50 border border-sky-200 rounded-3xl p-8 sm:p-10 text-center no-print">
          <h2 className="text-2xl sm:text-3xl font-medium text-slate-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Generate your first article in 60 seconds
          </h2>
          <p className="text-slate-600 max-w-xl mx-auto mb-6 text-sm">
            Open your dashboard → "SEO Articles" tab → tap "Generate". Our AI writes a patient-friendly article tailored to your specialty and city. You read it, edit anything you want, and publish. Patients start finding it on Google in days.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/dashboard?tab=seo_articles"
              className="inline-flex items-center gap-2 px-6 py-3 bg-sky-600 text-white rounded-xl text-base font-semibold hover:bg-sky-700"
              data-testid="launch-articles-go-dashboard">
              Open my dashboard <ArrowRight size={16} />
            </Link>
          </div>
        </div>

        <footer className="mt-14 text-center text-xs text-slate-400">
          Published 10 Feb 2026 · DocSite product team · <Link to="/whats-new" className="text-sky-600 hover:underline">More updates →</Link>
        </footer>
      </div>
    </div>
  );
}

function Card({ icon, title, children }) {
  return (
    <div className="rounded-2xl border border-slate-200 p-5 bg-white">
      <div className="flex items-center gap-2 mb-2">{icon}<p className="font-semibold text-slate-900">{title}</p></div>
      <p className="text-sm text-slate-600 leading-relaxed">{children}</p>
    </div>
  );
}
function Section({ title, icon, children }) {
  return (
    <section className="mb-10">
      <h2 className="text-2xl sm:text-3xl font-medium text-slate-900 mb-5 flex items-center gap-2.5" style={{ fontFamily: 'Outfit, sans-serif' }}>
        {icon}{title}
      </h2>
      {children}
    </section>
  );
}
function Item({ children }) {
  return (
    <li className="flex items-start gap-3">
      <Stethoscope size={14} className="text-sky-600 shrink-0 mt-1" />
      <span className="leading-relaxed">{children}</span>
    </li>
  );
}
function FactRow({ icon, title, body }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-center gap-1.5 mb-1">{icon}<p className="font-semibold text-slate-900 text-sm">{title}</p></div>
      <p className="text-xs text-slate-600 leading-relaxed">{body}</p>
    </div>
  );
}
